import { PRO_PRICE_PER_MONTH } from "~/utils/pricing";
import { useEffect, useMemo, useState } from "react";
import * as DocumentPicker from "expo-document-picker";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Alert, StyleSheet, View } from "react-native";

import { ProtectedScreen } from "~/components/ui";
import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppText,
  Button,
  ButtonText,
  Card,
  DetailRow,
  EmptyState,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoiceItems,
  useWorkspaceInvoiceLifecycle,
  useWorkspaceInvoiceReminders,
  useWorkspaceInvoices,
  useWorkspaceMembership,
} from "~/hooks/workspace";
import { useAuth } from "~/hooks/useAuth";
import { useWorkspaceSubscription } from "~/hooks/useSubscription";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { openExternalUrl } from "~/utils/external-link";
import { printInvoiceDocument } from "~/utils/invoice-document";
import { removeFile, signedFileUrl, uploadFile } from "~/api/uploads";
import { db } from "~/api/db";

function safeFilename(value: string) {
  return value.replace(/[^a-zA-Z0-9._-]/g, "-").slice(-120);
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function lineSubtotal(item: { quantity: number; unit_amount_cents: number }) {
  return Math.round(item.quantity * item.unit_amount_cents);
}

function lineGst(item: {
  quantity: number;
  unit_amount_cents: number;
  gst_rate: number;
}) {
  return Math.round(lineSubtotal(item) * (item.gst_rate / 100));
}

function normalizeParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function todayPlus(days: number) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

const INVOICE_STATUS_ACTIONS = [
  { label: "Draft", value: "draft" },
  { label: "Mark sent", value: "sent" },
  { label: "Mark paid", value: "paid" },
  { label: "Overdue", value: "overdue" },
  { label: "Void", value: "void" },
] as const;

export default function InvoiceDetailScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const { user } = useAuth();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const invoiceId = normalizeParam(params.id);
  const [reminderDate, setReminderDate] = useState(todayPlus(7));
  const [reminderDeliveryResult, setReminderDeliveryResult] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [publicLinkError, setPublicLinkError] = useState("");
  const [scheduleDate, setScheduleDate] = useState(todayPlus(1));
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayPlus(0));
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [editPurchaseOrderReference, setEditPurchaseOrderReference] =
    useState("");
  const [editClientReference, setEditClientReference] = useState("");
  const [editJobStartsOn, setEditJobStartsOn] = useState("");
  const [editJobEndsOn, setEditJobEndsOn] = useState("");
  const workspace = usePrimaryWorkspace();
  const subscription = useWorkspaceSubscription(workspace.data?.id);
  const workspaceMembership = useWorkspaceMembership(workspace.data?.id);
  const clients = useWorkspaceClients(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);
  const invoiceIds = useMemo(() => (invoiceId ? [invoiceId] : []), [invoiceId]);
  const invoiceItems = useWorkspaceInvoiceItems(workspace.data?.id, invoiceIds);
  const invoiceLifecycle = useWorkspaceInvoiceLifecycle(
    workspace.data?.id,
    invoiceIds,
  );
  const invoiceReminders = useWorkspaceInvoiceReminders(
    workspace.data?.id,
    invoiceIds,
  );

  const invoice =
    (invoices.data ?? []).find((record) => record.id === invoiceId) ?? null;
  const client = invoice?.client_id
    ? (clients.data ?? []).find((record) => record.id === invoice.client_id)
    : null;

  useEffect(() => {
    if (!invoice) return;
    setEditPurchaseOrderReference(invoice.purchase_order_reference ?? "");
    setEditClientReference(invoice.client_reference ?? "");
    setEditJobStartsOn(invoice.job_starts_on ?? "");
    setEditJobEndsOn(invoice.job_ends_on ?? "");
  }, [invoice?.id]);
  const items = invoiceItems.items.filter(
    (item) => item.invoice_id === invoice?.id,
  );
  const refunds = invoiceLifecycle.refunds.filter(
    (refund) => refund.invoice_id === invoice?.id,
  );
  const payments = invoiceLifecycle.payments.filter(
    (payment) => payment.invoice_id === invoice?.id,
  );
  const amendments = invoiceLifecycle.amendments.filter(
    (amendment) => amendment.invoice_id === invoice?.id,
  );
  const paidTotal = payments.reduce(
    (total, payment) => total + payment.amount_cents,
    0,
  );
  const reminders = invoiceReminders.reminders.filter(
    (reminder) => reminder.invoice_id === invoice?.id,
  );
  const refundedTotal = refunds.reduce(
    (total, refund) => total + refund.amount_cents,
    0,
  );
  const remainingBalance = invoice
    ? Math.max(0, invoice.total_cents - paidTotal)
    : 0;
  const error =
    workspace.error ??
    clients.error ??
    invoices.error ??
    invoices.sendEmail.error ??
    invoices.scheduleSend.error ??
    invoices.cancelScheduledSend.error ??
    invoices.updateStatus.error ??
    invoiceItems.error ??
    invoiceLifecycle.error ??
    invoiceLifecycle.createPayment.error ??
    invoiceReminders.error ??
    invoiceReminders.create.error ??
    invoiceReminders.sendDue.error;
  const errorMessage = error instanceof Error ? error.message : "";
  const canSendInvoice = Boolean(client?.email && items.length > 0);
  const canScheduleSend = invoice?.status === "draft" && canSendInvoice;
  const isSendScheduled = invoice?.scheduled_send_status === "scheduled";
  const canManageWorkspace = Boolean(
    workspace.data?.owner_id === user?.id ||
    workspaceMembership.data?.role === "admin" ||
    workspaceMembership.data?.role === "owner",
  );
  const canUseAutomatedReminders =
    subscription.canUseAutomatedReminders && canManageWorkspace;

  async function scheduleReminder() {
    if (!invoice || !reminderDate.trim() || !canUseAutomatedReminders) return;
    await invoiceReminders.create.mutateAsync({
      invoiceId: invoice.id,
      scheduledFor: reminderDate,
    });
    setReminderDate(todayPlus(7));
  }

  async function scheduleInvoiceSend() {
    if (!invoice || !canScheduleSend) return;
    await invoices.scheduleSend.mutateAsync({
      id: invoice.id,
      date: scheduleDate,
      time: scheduleTime,
    });
  }

  async function saveInvoiceReferences() {
    if (!invoice) return;
    await invoices.update.mutateAsync({
      id: invoice.id,
      dueDate: invoice.due_date ?? todayPlus(14),
      notes: invoice.notes ?? "",
      clientId: invoice.client_id ?? "",
      purchaseOrderReference: editPurchaseOrderReference,
      clientReference: editClientReference,
      jobStartsOn: editJobStartsOn,
      jobEndsOn: editJobEndsOn,
    });
  }

  async function pickInvoiceAttachment() {
    if (!invoice || !user || !workspace.data?.id) return;
    const result = await DocumentPicker.getDocumentAsync({
      type: [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      copyToCacheDirectory: true,
    });
    if (result.canceled) return;
    const asset = result.assets[0];
    if (!asset) return;
    if (asset.size && asset.size > 10 * 1024 * 1024) {
      Alert.alert("File too large", "Choose a file up to 10 MB.");
      return;
    }
    const mimeType = asset.mimeType ?? "application/octet-stream";
    const fileBody = asset.file
      ? await asset.file.arrayBuffer()
      : await (await fetch(asset.uri)).arrayBuffer();
    let path: string;
    try {
      ({ path } = await uploadFile({
        workspaceId: workspace.data.id,
        purpose: "attachment",
        body: fileBody,
        mimeType,
      }));
    } catch (error) {
      Alert.alert(
        "Upload failed",
        error instanceof Error ? error.message : "Try again.",
      );
      return;
    }
    if (invoice.attachment_path && invoice.attachment_path !== path) {
      await removeFile(invoice.attachment_path);
    }
    await invoices.updateAttachment.mutateAsync({
      id: invoice.id,
      attachmentPath: path,
      attachmentFilename: asset.name,
      attachmentMimeType: mimeType,
      attachmentSizeBytes: asset.size ?? fileBody.byteLength,
    });
  }

  async function removeInvoiceAttachment() {
    if (!invoice?.attachment_path) return;
    await removeFile(invoice.attachment_path);
    await invoices.updateAttachment.mutateAsync({
      id: invoice.id,
      attachmentPath: null,
      attachmentFilename: null,
      attachmentMimeType: null,
      attachmentSizeBytes: null,
    });
  }

  async function cancelInvoiceScheduledSend() {
    if (!invoice) return;
    await invoices.cancelScheduledSend.mutateAsync(invoice.id);
  }

  async function sendDueReminders() {
    if (!invoice || !canUseAutomatedReminders) return;
    const result = await invoiceReminders.sendDue.mutateAsync({
      invoiceId: invoice.id,
    });
    if (result) {
      setReminderDeliveryResult(
        `Sent ${result.sent}, skipped ${result.skipped}, failed ${result.failed}.`,
      );
    }
  }

  async function recordPayment() {
    if (!invoice) return;
    await invoiceLifecycle.createPayment.mutateAsync({
      amount: paymentAmount,
      invoice,
      note: paymentNote,
      paidAt: paymentDate,
      reference: paymentReference,
    });
    setPaymentAmount("");
    setPaymentReference("");
    setPaymentNote("");
    setPaymentDate(todayPlus(0));
  }

  function openInvoiceDocument() {
    if (!workspace.data || !invoice) return;
    setDocumentError("");
    const opened = printInvoiceDocument({
      client: client ?? null,
      invoice,
      items,
      workspace: workspace.data,
    });
    if (!opened) {
      setDocumentError(
        "MintStint could not open the invoice document. Allow pop-ups for this site and try again.",
      );
    }
  }

  async function openPublicInvoice() {
    if (!invoice) return;
    setPublicLinkError("");
    try {
      const result = await invoices.issuePublicLink.mutateAsync(invoice.id);
      openExternalUrl(result.url);
    } catch (linkError) {
      setPublicLinkError(
        linkError instanceof Error
          ? linkError.message
          : "MintStint could not open the client invoice link.",
      );
    }
  }

  if (workspace.isLoading || invoices.isLoading) {
    return (
      <ProtectedScreen title="Invoice" subtitle="Loading invoice details.">
        <Card gap="sm">
          <AppText>Loading...</AppText>
        </Card>
      </ProtectedScreen>
    );
  }

  if (!invoice) {
    return (
      <ProtectedScreen title="Invoice not found">
        <Card gap="md">
          <AppText size="sm">
            This invoice could not be found in the current workspace.
          </AppText>
          <Button
            align="start"
            variant="outline"
            onPress={() => router.push("/invoices" as never)}
          >
            <ButtonText>Back to invoices</ButtonText>
          </Button>
        </Card>
      </ProtectedScreen>
    );
  }

  return (
    <ProtectedScreen
      title={invoice.invoice_number}
      subtitle="Review invoice details, client context, line items, reminders, refunds, and amendment history."
    >
      {errorMessage ? (
        <Card danger gap="xs">
          <AppText tone="danger" weight="semibold">
            Invoice details could not load
          </AppText>
          <AppText tone="danger" size="xs">
            {errorMessage}
          </AppText>
        </Card>
      ) : null}

      <View style={stylesheet.summaryGrid}>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs" tone="muted">
            Status
          </AppText>
          <AppText size="lg" weight="bold">
            {invoice.status}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs" tone="muted">
            Total
          </AppText>
          <AppText size="lg" weight="bold">
            {formatMoney(invoice.total_cents)}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs" tone="muted">
            Due
          </AppText>
          <AppText size="lg" weight="bold">
            {invoice.due_date ?? "Not set"}
          </AppText>
        </Card>
        <Card gap="xs" style={stylesheet.summaryCard}>
          <AppText size="xs" tone="muted">
            Refunded
          </AppText>
          <AppText size="lg" weight="bold">
            {formatMoney(refundedTotal)}
          </AppText>
        </Card>
      </View>

      <Card gap="md">
        <SectionHeader
          title="Invoice actions"
          description="Send, mark lifecycle state, and handle reminder follow-up from this invoice record."
        />
        <View style={stylesheet.actionBand}>
          <Button
            compact
            iconName="printer"
            variant="outline"
            disabled={items.length === 0}
            onPress={openInvoiceDocument}
          >
            <ButtonText>Print / save PDF</ButtonText>
          </Button>
          <Button
            compact
            iconName="send"
            disabled={!canSendInvoice || invoices.sendEmail.isPending}
            onPress={() => void invoices.sendEmail.mutateAsync(invoice.id)}
          >
            <ButtonText>
              {invoices.sendEmail.isPending ? "Sending..." : "Send invoice"}
            </ButtonText>
          </Button>
          <Button
            compact
            iconName="external-link"
            variant="outline"
            disabled={items.length === 0 || invoices.issuePublicLink.isPending}
            onPress={() => void openPublicInvoice()}
          >
            <ButtonText>
              {invoices.issuePublicLink.isPending
                ? "Opening..."
                : "Open client view"}
            </ButtonText>
          </Button>
          {INVOICE_STATUS_ACTIONS.map((status) => (
            <Button
              key={status.value}
              compact
              variant={invoice.status === status.value ? "primary" : "outline"}
              disabled={invoices.updateStatus.isPending}
              onPress={() =>
                void invoices.updateStatus.mutateAsync({
                  id: invoice.id,
                  status: status.value,
                })
              }
            >
              <ButtonText>{status.label}</ButtonText>
            </Button>
          ))}
        </View>
        {!client?.email ? (
          <AppText size="xs" tone="muted">
            Add a client billing email before sending.
          </AppText>
        ) : null}
        {items.length === 0 ? (
          <AppText size="xs" tone="muted">
            Add at least one line item before sending.
          </AppText>
        ) : null}
        {documentError ? (
          <AppText size="xs" tone="danger">
            {documentError}
          </AppText>
        ) : null}
        {publicLinkError ? (
          <AppText size="xs" tone="danger">
            {publicLinkError}
          </AppText>
        ) : null}
      </Card>

      {canScheduleSend || isSendScheduled || invoice.scheduled_send_status ? (
        <Card gap="md">
          <SectionHeader
            title="Schedule send"
            description="Queue a one-off send for this draft invoice (NZ time, +12:00)."
          />
          {isSendScheduled || invoice.scheduled_send_at ? (
            <Stack gap="xs">
              <DetailRow
                label="Scheduled for"
                value={
                  invoice.scheduled_send_at
                    ? new Date(invoice.scheduled_send_at).toLocaleString()
                    : "Not set"
                }
              />
              <DetailRow
                label="Status"
                value={invoice.scheduled_send_status ?? "none"}
              />
              {invoice.scheduled_send_error ? (
                <AppText size="xs" tone="danger">
                  {invoice.scheduled_send_error}
                </AppText>
              ) : null}
            </Stack>
          ) : null}
          {canScheduleSend && !isSendScheduled ? (
            <View style={stylesheet.reminderControls}>
              <View style={stylesheet.reminderField}>
                <AppText size="xs" weight="semibold">
                  Date (YYYY-MM-DD)
                </AppText>
                <TextField
                  value={scheduleDate}
                  placeholder="2026-08-27"
                  accessibilityLabel="Schedule send date"
                  onChangeText={setScheduleDate}
                />
              </View>
              <View style={stylesheet.smallField}>
                <AppText size="xs" weight="semibold">
                  Time (HH:mm)
                </AppText>
                <TextField
                  value={scheduleTime}
                  placeholder="09:00"
                  accessibilityLabel="Schedule send time"
                  onChangeText={setScheduleTime}
                />
              </View>
              <Button
                compact
                iconName="clock"
                disabled={
                  !scheduleDate.trim() ||
                  !scheduleTime.trim() ||
                  invoices.scheduleSend.isPending
                }
                onPress={() => void scheduleInvoiceSend()}
              >
                <ButtonText>
                  {invoices.scheduleSend.isPending
                    ? "Scheduling..."
                    : "Schedule send"}
                </ButtonText>
              </Button>
            </View>
          ) : null}
          {isSendScheduled ? (
            <Button
              compact
              align="start"
              variant="outline"
              disabled={invoices.cancelScheduledSend.isPending}
              onPress={() => void cancelInvoiceScheduledSend()}
            >
              <ButtonText>
                {invoices.cancelScheduledSend.isPending
                  ? "Cancelling..."
                  : "Cancel scheduled send"}
              </ButtonText>
            </Button>
          ) : null}
          {!canSendInvoice ? (
            <AppText size="xs" tone="muted">
              Add a client email and at least one line item before scheduling.
            </AppText>
          ) : null}
        </Card>
      ) : null}

      {canUseAutomatedReminders ? (
        <Card gap="md">
          <SectionHeader
            title="Reminder controls"
            description="Schedule follow-up or process due reminders for this invoice."
          />
          <View style={stylesheet.reminderControls}>
            <View style={stylesheet.reminderField}>
              <AppText size="xs" weight="semibold">
                Reminder date
              </AppText>
              <TextField
                value={reminderDate}
                placeholder="2026-07-20"
                onChangeText={setReminderDate}
              />
            </View>
            <Button
              compact
              iconName="bell-plus"
              disabled={
                !reminderDate.trim() || invoiceReminders.create.isPending
              }
              onPress={() => void scheduleReminder()}
            >
              <ButtonText>
                {invoiceReminders.create.isPending
                  ? "Scheduling..."
                  : "Schedule reminder"}
              </ButtonText>
            </Button>
            <Button
              compact
              iconName="send"
              variant="outline"
              disabled={invoiceReminders.sendDue.isPending}
              onPress={() => void sendDueReminders()}
            >
              <ButtonText>
                {invoiceReminders.sendDue.isPending
                  ? "Sending reminders..."
                  : "Send due reminders"}
              </ButtonText>
            </Button>
          </View>
          {reminderDeliveryResult ? (
            <AppText size="xs" tone="muted">
              {reminderDeliveryResult}
            </AppText>
          ) : null}
        </Card>
      ) : !subscription.canUseAutomatedReminders ? (
        <UpgradePrompt
          reason={`Subscribe to MintStint Pro for ${PRO_PRICE_PER_MONTH} to keep recurring invoices, reminders, reports, and exports active.`}
        />
      ) : (
        <Card gap="xs">
          <AppText weight="semibold">Reminder access</AppText>
          <AppText size="xs" tone="muted">
            Workspace owners and admins manage automated reminder schedules.
          </AppText>
        </Card>
      )}

      <View style={stylesheet.detailGrid}>
        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Client"
            description={client?.name ?? "No client selected for this invoice."}
          />
          {client ? (
            <Stack gap="sm">
              <DetailRow label="Email" value={client.email ?? "None"} />
              <DetailRow
                label="Contact"
                value={client.contact_name ?? "None"}
              />
              <DetailRow label="GST" value={client.gst_number ?? "None"} />
            </Stack>
          ) : null}
        </Card>

        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Timeline"
            description="Issue, sent, paid, and due dates."
          />
          <Stack gap="sm">
            <DetailRow label="Issued" value={invoice.issue_date} />
            <DetailRow label="Sent" value={invoice.sent_at ?? "Not sent"} />
            <DetailRow
              label="Viewed"
              value={invoice.viewed_at ?? "Not viewed"}
            />
            <DetailRow label="Paid" value={invoice.paid_at ?? "Unpaid"} />
            <DetailRow label="Due" value={invoice.due_date ?? "Not set"} />
          </Stack>
        </Card>
      </View>

      <Card gap="md">
        <SectionHeader
          title="References & job dates"
          description="Purchase order, client reference, and scheduled job window."
        />
        <View style={stylesheet.formGrid}>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Purchase order
            </AppText>
            <TextField
              accessibilityLabel="Purchase order reference"
              value={editPurchaseOrderReference}
              placeholder="PO-1042"
              onChangeText={setEditPurchaseOrderReference}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Client reference
            </AppText>
            <TextField
              accessibilityLabel="Client reference"
              value={editClientReference}
              placeholder="Job or cost centre"
              onChangeText={setEditClientReference}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Job starts
            </AppText>
            <TextField
              accessibilityLabel="Job starts on"
              value={editJobStartsOn}
              placeholder="YYYY-MM-DD"
              onChangeText={setEditJobStartsOn}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Job ends
            </AppText>
            <TextField
              accessibilityLabel="Job ends on"
              value={editJobEndsOn}
              placeholder="YYYY-MM-DD"
              onChangeText={setEditJobEndsOn}
            />
          </View>
        </View>
        <Button
          align="start"
          compact
          disabled={invoices.update.isPending}
          onPress={() => void saveInvoiceReferences()}
        >
          <ButtonText>
            {invoices.update.isPending ? "Saving..." : "Save references"}
          </ButtonText>
        </Button>
        <Stack gap="xs">
          <AppText size="xs" weight="semibold">
            Email attachment
          </AppText>
          <AppText size="xs" tone="muted">
            {invoice.attachment_filename
              ? invoice.attachment_filename
              : "No attachment"}
          </AppText>
          <View style={stylesheet.actionBand}>
            <Button
              compact
              variant="outline"
              disabled={invoices.updateAttachment.isPending}
              onPress={() => void pickInvoiceAttachment()}
            >
              <ButtonText>
                {invoice.attachment_filename
                  ? "Replace attachment"
                  : "Attach file"}
              </ButtonText>
            </Button>
            {invoice.attachment_filename ? (
              <Button
                compact
                variant="outline"
                disabled={invoices.updateAttachment.isPending}
                onPress={() => void removeInvoiceAttachment()}
              >
                <ButtonText>Remove</ButtonText>
              </Button>
            ) : null}
          </View>
        </Stack>
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Line items"
          description="GST and totals are calculated from these rows."
        />
        {items.length === 0 ? (
          <EmptyState title="No line items have been added yet." />
        ) : (
          <Stack gap="sm">
            {items.map((item) => (
              <View key={item.id} style={stylesheet.itemRow}>
                <View style={stylesheet.itemMain}>
                  <AppText weight="semibold">{item.description}</AppText>
                  <AppText size="xs" tone="muted">
                    {item.quantity} x {formatMoney(item.unit_amount_cents)} -
                    GST {item.gst_rate}% - {item.source}
                  </AppText>
                </View>
                <View style={stylesheet.itemTotals}>
                  <AppText size="xs" tone="muted">
                    GST {formatMoney(lineGst(item))}
                  </AppText>
                  <AppText weight="semibold">
                    {formatMoney(lineSubtotal(item) + lineGst(item))}
                  </AppText>
                  {item.time_entry_id ? (
                    <Button
                      compact
                      variant="outline"
                      onPress={() =>
                        router.push(
                          `/time-entries/${item.time_entry_id}` as never,
                        )
                      }
                    >
                      <ButtonText>View time</ButtonText>
                    </Button>
                  ) : null}
                </View>
              </View>
            ))}
          </Stack>
        )}
        <View style={stylesheet.totals}>
          <DetailRow
            label="Subtotal"
            value={formatMoney(invoice.subtotal_cents)}
          />
          <DetailRow label="GST" value={formatMoney(invoice.gst_cents)} />
          {invoice.rounding_adjustment_cents !== 0 ? (
            <DetailRow
              label="Rounding"
              value={formatMoney(invoice.rounding_adjustment_cents)}
            />
          ) : null}
          <DetailRow
            label="Total"
            value={
              <AppText weight="bold">
                {formatMoney(invoice.total_cents)}
              </AppText>
            }
          />
        </View>
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Payments"
          description="Record payments against this invoice and keep a clear settlement history."
        />
        <View style={stylesheet.formGrid}>
          <View style={stylesheet.smallField}>
            <AppText size="xs" weight="semibold">
              Payment
            </AppText>
            <TextField
              value={paymentAmount}
              placeholder={(remainingBalance / 100).toFixed(2)}
              keyboardType="numeric"
              onChangeText={setPaymentAmount}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Paid on
            </AppText>
            <TextField
              value={paymentDate}
              placeholder="2026-07-04"
              onChangeText={setPaymentDate}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Reference
            </AppText>
            <TextField
              value={paymentReference}
              placeholder="Bank transfer, Stripe payout..."
              onChangeText={setPaymentReference}
            />
          </View>
          <View style={[stylesheet.field, stylesheet.wideField]}>
            <AppText size="xs" weight="semibold">
              Payment note
            </AppText>
            <TextField
              value={paymentNote}
              placeholder="Paid in two parts, reconciled manually..."
              onChangeText={setPaymentNote}
            />
          </View>
        </View>
        <View style={stylesheet.actionBand}>
          <Button
            compact
            iconName="badge-dollar-sign"
            variant="outline"
            disabled={
              !paymentAmount.trim() ||
              remainingBalance <= 0 ||
              ["void", "refunded"].includes(invoice.status) ||
              invoiceLifecycle.createPayment.isPending
            }
            onPress={() => void recordPayment()}
          >
            <ButtonText>
              {invoiceLifecycle.createPayment.isPending
                ? "Recording..."
                : "Record payment"}
            </ButtonText>
          </Button>
          {["void", "refunded"].includes(invoice.status) ? (
            <AppText size="xs" tone="muted">
              Void or fully refunded invoices cannot take payments.
            </AppText>
          ) : null}
        </View>
        <Stack gap="sm">
          <DetailRow label="Paid" value={formatMoney(paidTotal)} />
          <DetailRow
            label="Remaining balance"
            value={formatMoney(remainingBalance)}
          />
        </Stack>
        {payments.length === 0 ? (
          <AppText size="sm">No payments recorded yet.</AppText>
        ) : (
          <Stack gap="sm">
            {payments.map((payment) => (
              <View key={payment.id} style={stylesheet.historyRow}>
                <AppText weight="semibold">
                  {formatMoney(payment.amount_cents)}
                </AppText>
                <AppText size="xs" tone="muted">
                  {new Date(payment.paid_at).toLocaleString()}
                  {payment.reference ? ` - ${payment.reference}` : ""}
                </AppText>
                {payment.note ? (
                  <AppText size="xs" tone="muted">
                    {payment.note}
                  </AppText>
                ) : null}
              </View>
            ))}
          </Stack>
        )}
      </Card>

      <View style={stylesheet.detailGrid}>
        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Reminders"
            description="Scheduled and delivered reminder history."
          />
          {reminders.length === 0 ? (
            <AppText size="sm">No reminders for this invoice.</AppText>
          ) : (
            <Stack gap="sm">
              {reminders.map((reminder) => (
                <View key={reminder.id} style={stylesheet.historyRow}>
                  <AppText weight="semibold">{reminder.status}</AppText>
                  <AppText size="xs" tone="muted">
                    Scheduled{" "}
                    {new Date(reminder.scheduled_for).toLocaleString()}
                  </AppText>
                  {reminder.sent_at ? (
                    <AppText size="xs" tone="muted">
                      Sent {reminder.sent_at}
                    </AppText>
                  ) : null}
                  {reminder.delivery_error ? (
                    <AppText size="xs" tone="danger">
                      {reminder.delivery_error}
                    </AppText>
                  ) : null}
                </View>
              ))}
            </Stack>
          )}
        </Card>

        <Card gap="md" style={stylesheet.detailCard}>
          <SectionHeader
            title="Refunds and amendments"
            description="Recorded lifecycle changes for this invoice."
          />
          {refunds.length === 0 && amendments.length === 0 ? (
            <AppText size="sm">No refunds or amendments recorded.</AppText>
          ) : (
            <Stack gap="md">
              {refunds.map((refund) => (
                <View key={refund.id} style={stylesheet.historyRow}>
                  <AppText weight="semibold">
                    Refund {formatMoney(refund.amount_cents)}
                  </AppText>
                  <AppText size="xs" tone="muted">
                    {new Date(refund.refunded_at).toLocaleString()} -{" "}
                    {refund.reason ?? "No reason"}
                  </AppText>
                </View>
              ))}
              {amendments.map((amendment) => (
                <View key={amendment.id} style={stylesheet.historyRow}>
                  <AppText weight="semibold">{amendment.reason}</AppText>
                  <AppText size="xs" tone="muted">
                    {new Date(amendment.created_at).toLocaleString()}
                  </AppText>
                </View>
              ))}
            </Stack>
          )}
        </Card>
      </View>

      <View style={stylesheet.actions}>
        <Button
          align="start"
          variant="outline"
          onPress={() => router.push("/invoices" as never)}
        >
          <ButtonText>Back to invoices</ButtonText>
        </Button>
      </View>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    summaryCard: { flexGrow: 1, flexShrink: 1, flexBasis: 150, minWidth: 140 },
    detailCard: { flexGrow: 1, flexShrink: 1, flexBasis: 320, minWidth: 260 },
    summaryGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    detailGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    actionBand: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
    reminderControls: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "flex-end",
      gap: spacing.s,
    },
    reminderField: {
      minWidth: 220,
      gap: spacing.xs,
    },
    formGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    field: {
      flex: 1,
      minWidth: 180,
      gap: spacing.xs,
    },
    smallField: {
      width: 140,
      minWidth: 140,
      gap: spacing.xs,
    },
    wideField: {
      minWidth: 280,
    },
    itemRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.hairline,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    itemMain: {
      flex: 1,
      minWidth: 220,
      gap: spacing.xs,
    },
    itemTotals: {
      alignItems: "flex-end",
      gap: spacing.xs,
    },
    totals: {
      gap: spacing.s,
      paddingTop: spacing.m,
      borderTopWidth: 1,
      borderTopColor: colors.hairline,
    },
    historyRow: {
      gap: spacing.xs,
      paddingVertical: spacing.s,
      borderBottomWidth: 1,
      borderBottomColor: colors.hairline,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
  });
